import { Order } from '@trukt/types/Order';

import { AxiosInstance } from 'axios';

import { JobEquipmentTypeLiterals, JobInspectionTypeLiterals } from '@trukt/types/Job';
import { BaseRequest } from '@trukt/types/BaseRequest';

import {
  GetOrderRequest,
  GetOrderResponse,
  OrderUpdateRequest,
  OrderUpdateResponse,
  SearchOrderRequest,
  SearchOrderResponse,
} from './OrderServices.types';
import { OrderCommodityServices, OrderJobsService, OrderPartyService } from './services';

import { OrderFinanceService } from '../OrderFinanceService';
import { OrderBulkService } from '../OrderBulkService';
import { OrderClassificationService } from '../OrderClassificationService';
import { OrderTenderService } from '../OrderTenderService';
import {
  OrderJobCreateRequestContact,
  OrderJobCreateRequestStop,
} from '../../shared/types/order-job';

export interface OrderCreateRequestBody extends Partial<
  Pick<
    Order,
    | 'instructions'
    | 'referenceNumber'
    | 'bol'
    | 'number'
    | 'isDummy'
    | 'dateExpectedCompleteBy'
    | 'distance'
  >
> {
  ownerCompanyGuid?: string;
  dispatcherGuid?: string;
  salespersonGuid?: string;
  clientGuid: string;
  clientContact?: OrderJobCreateRequestContact;
  inspectionType: JobInspectionTypeLiterals;
  classificationId: string;
  subClassificationId: string;
  equipmentTypes?: JobEquipmentTypeLiterals[];
  stops: OrderJobCreateRequestStop[];
}

export type OrderCreateRequest = Pick<
  BaseRequest<{ format?: 'SIMPLE' }, OrderCreateRequestBody, void>,
  'queryParams' | 'body'
>;

export type OrderCreateResponse = { guid: string };

export type OrderSearchByVinRequest = BaseRequest<void, void, { vin: string }>;
export type OrderSearchByVinResponse = {
  guid: string;
  number: string;
  jobs: {
    guid: string;
    number: string;
  }[];
}[];

export class OrderService {
  instance: AxiosInstance;
  commodities: OrderCommodityServices;
  job: OrderJobsService;
  party: OrderPartyService;
  finance: OrderFinanceService;
  bulk: OrderBulkService;
  classification: OrderClassificationService;
  tender: OrderTenderService;

  constructor(instance: AxiosInstance) {
    this.instance = instance;
    this.commodities = new OrderCommodityServices(instance);
    this.job = new OrderJobsService(instance);
    this.party = new OrderPartyService(instance);
    this.finance = new OrderFinanceService(instance);
    this.bulk = new OrderBulkService(instance);
    this.classification = new OrderClassificationService(instance);
    this.tender = new OrderTenderService(instance);
  }

  getById(request: GetOrderRequest) {
    return this.instance.get<GetOrderResponse>(`/orders/${request.routeParams.id}`);
  }

  create({ queryParams, body }: OrderCreateRequest) {
    return this.instance.post<OrderCreateResponse>(`/orders`, body, {
      params: queryParams,
    });
  }

  search(request: SearchOrderRequest) {
    return this.instance.post<SearchOrderResponse>('/orders/list', request.body, {
      params: request.queryParams,
    });
  }

  update({ routeParams, body }: OrderUpdateRequest) {
    return this.instance.patch<OrderUpdateResponse>(`/orders/${routeParams.orderId}`, body);
  }

  findByVin({ routeParams }: OrderSearchByVinRequest) {
    return this.instance.get<OrderSearchByVinResponse>(`/order/find_by_vin/${routeParams.vin}`);
  }
}
